import React from 'react';
import {
  Box, Skeleton, Stack, Table, TableHead, TableRow, TableCell, TableBody,
  useMediaQuery, useTheme,
} from '@mui/material';
import Panel from '../components/common/Panel';
import { SP } from '../theme/tokens';

/**
 * POInboxSkeleton — Ladezustand der LISTENFLAECHE der Beschaffungs-Inbox.
 *
 * 🔴 NUR die Liste, nicht die Seite. Kopfzeile (`ViewHeader`), Suche und Reiter
 * rendert `POInbox.jsx` unbedingt — ein Skelett, das sie nochmal zeichnet,
 * verdoppelt sie. Deshalb steht hier kein `PageContainer` und keine Kopfzeile;
 * das Skelett sitzt an genau der Stelle, an der sonst die Liste steht (der
 * `loading && !hasLoadedOnce`-Zweig).
 *
 * `anti-patterns.md` §11 verlangt „Skelett statt Spinner". Der Rahmen ist ein
 * `Panel` (R16-A W5), kein rohes `Paper` — dieselbe Flaeche wie die fertige
 * Liste. Auf schmalen Viewports ist die Liste eine Kartenfolge (`InboxCard`),
 * darum formt das Skelett sie dort ebenfalls als Karten, nicht als Tabelle.
 *
 * Werte aus `theme/tokens` — Rule 22 laesst neue Literale nicht zu.
 */
export default function POInboxSkeleton({ rows = 8 }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      // 🔴 G4b-Nachbesserung 2 (2026-10-18): die MESS-Marke des Ladezustands
      // (`selektoren.js` §`skelett`). `oeffne` wartet auf ihr Verschwinden,
      // bevor eine §15-Probe misst — ohne sie lief P32 gegen das Geruest und
      // meldete „liste-tabelle fehlt", obwohl die Zone da ist.
      // 🔴 G4b-Nachbesserung 3 (R16 W1): das Geruest ist fuer Screenreader
      // KEINE Tabelle mit leeren Zellen — es ist Dekoration, die den Aufbau
      // verspricht. Hausform `RMACasesDashboardBody.jsx:962`.
      <Stack spacing={1} data-testid="liste-skelett" aria-hidden="true">
        {[...Array(rows)].map((_, i) => (
          <Panel key={i} pad="card">
            <Skeleton variant="text" width="45%" height={20} />
            <Skeleton variant="text" width="75%" />
            <Skeleton variant="text" width="30%" />
          </Panel>
        ))}
      </Stack>
    );
  }

  return (
    // 🔴 Die Zonen-Marke `liste-tabelle` traegt BEIDE Zustaende — Geruest und
    // Tabelle (Hausform `RMACasesDashboardBody.jsx:958/970`, G3b
    // `SecondLifeBestand.jsx:1597/1678`). Sie bleibt damit die Messflaeche der
    // R-Anker; die Lade-Marke `liste-skelett` sitzt am `<Table>` darunter.
    <Panel pad="none" clip data-testid="liste-tabelle" aria-busy="true">
      {/* Auswahl-/Zaehlleiste ueber der Tabelle, wie in der fertigen Flaeche.
          §6 PR 1 / Schritt 1.3: `py` traegt denselben Token wie die geladene
          Leiste (`POInbox.jsx`, A5). */}
      <Box data-testid="po-inbox-skeleton-toolbar" sx={{
        px: SP.sm, py: SP.chip, display: 'flex', alignItems: 'center', gap: SP.tight,
        borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover',
      }}>
        <Skeleton variant="text" width={90} height={16} />
      </Box>
      <Table size="small" data-testid="liste-skelett" aria-hidden="true">
        <TableHead>
          <TableRow>
            {[...Array(6)].map((_, i) => (
              <TableCell key={i}><Skeleton variant="text" width={75} /></TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(rows)].map((__, r) => (
            <TableRow key={r}>
              {[...Array(6)].map((__, c) => (
                <TableCell key={c}><Skeleton variant="text" width="65%" /></TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}
